import { ProjectStatus } from 'src/app/modules/shared/entities/project-status.enum';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { AppInfoService } from 'core/app-info/app-info.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DashboardTemplateValues } from 'src/app/modules/shared/entities/dashboard-template-values.class';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { PaginationPageEvent } from '@snapon/design-system-angular';
import { MatDialog } from '@angular/material/dialog';
import { ConfidenceToClose, ConfidenceToCloseNames } from 'src/app/modules/shared/entities/confidence-to-close.enum';
import { Sort } from '@angular/material/sort';
import { SortDirection, SortProjects } from 'src/app/modules/pos/quotes/entities/sort.enum';
import { UtilsService } from 'core/utils/utils.service';
import { DashboardService } from '../../services/dashboard.service';
import { Project } from '../../entities/project';
import { EditProjectDialogComponent } from './edit-project-dialog/edit-project-dialog.component';

@Component({
  selector: 'app-projects-table',
  templateUrl: './projects-table.component.html',
  styleUrls: ['./projects-table.component.scss'],
  standalone: false
})
export class ProjectsTableComponent implements OnInit {
  @Input() isHistory: boolean;
  @ViewChild('projectsTable') projectsTable: ElementRef;

  appStrings: AppLocalization;
  dateFormat: string;
  displayedColumns = [
    'projectOwnerName',
    'companyName',
    'contactName',
    'projectName',
    'projectType',
    'date',
    'lastUpdated',
    'projectStatus',
    'estimatedSalesValue',
    'estimatedSalesDate',
    'confidenceToClose',
    'edit'
  ];
  defaultSort = {
    direction: 'desc',
    sortBy: 'date'
  };
  sort = {
    direction: SortDirection[this.defaultSort.direction],
    sortBy: SortProjects[this.defaultSort.sortBy]
  };
  disabledStatus = ProjectStatus.Cancelled;

  confidenceToClose = ConfidenceToClose;
  confidenceToCloseNames = ConfidenceToCloseNames;

  projectsData: Project[];
  projectsTotal: number;
  totalPages: number;
  projectsPerPage = 15;
  currentPageItemCount = 0;
  pageOffset = 0;
  currencySign: string;
  isAccountManager = false;
  amEmployeeId: string;
  accountManagerId: string;
  statuses: { [key: number]: string };
  projectTypes: { [key: number]: string };
  loading = false;
  noResults = false;
  loadingTable = true;

  constructor(
    private appInfoService: AppInfoService,
    private dashboardService: DashboardService,
    private dialog: MatDialog,
    private utilsService: UtilsService,
    private deviceService: DeviceDetectorService
  ) { }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.dateFormat = this.appInfoService.dateFormat;
    this.isAccountManager = this.appInfoService.isAccountManager();
    this.currencySign = this.appInfoService.currencySymbol;
    this.amEmployeeId = this.appInfoService.userData.UserInfo.EmployeeId;
    this.dashboardService.getFollowupAccountManagers((list: AccountManager[]) => {
      list.forEach((item: AccountManager) => {
        if (item.employeeId === this.amEmployeeId) {
          this.accountManagerId = item.id;
        }
      });
      this.dashboardService.getStaticValues((templateValues: DashboardTemplateValues) => {
        this.statuses = templateValues.ProjectStatus;
        this.projectTypes = templateValues.ProjectType;
      });
      this.getProjects();
    });
  }

  getProjects(scrollToTop = false, setLoading = false) {
    if (setLoading) {
      this.loading = true;
    }
    if (scrollToTop) {
      this.scrollIntoView();
    }
    this.dashboardService.getProjects(
      this.isAccountManager ? this.accountManagerId : null,
      this.pageOffset,
      this.projectsPerPage,
      this.sort,
      this.isHistory,
    ).subscribe(result => {
      this.projectsData = result.filteredOpenProjects;
      this.projectsTotal = result.count;
      this.currentPageItemCount = result.filteredOpenProjects?.length ?? 0;
      this.noResults = this.currentPageItemCount === 0;
      this.totalPages = Math.ceil(result.count / this.projectsPerPage);

      this.loading = false;
      this.loadingTable = false;
    });
  }

  openEditProject(project?: Project) {
    const dialogRef = this.dialog.open(EditProjectDialogComponent, {
      panelClass: ['ds', 'fullWidth', this.deviceService.isMobile() ? 'fullScreen' : ''],
      data: {
        projectId: project?.projectId ?? null,
        isView: project?.projectStatusId === ProjectStatus.Cancelled
      },
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.shouldRefresh) {
        this.getProjects(true, true);
      }
    });
  }

  pageChanged($event: PaginationPageEvent) {
    this.loading = true;
    this.pageOffset = ($event.pageNumber - 1) * this.projectsPerPage;
    this.getProjects(true, true);
  }

  sortData($event: Sort) {
    this.loading = true;
    this.sort = {
      direction: SortDirection[$event.direction === '' ? 'none' : $event.direction],
      sortBy: SortProjects[$event.direction === '' ? 'none' : $event.active]
    };
    this.getProjects(true, true);
  }

  getProjectTypesDisplay(projectTypeIds: number[]) {
    const displayArray = projectTypeIds?.map(projectTypeId => this.projectTypes[projectTypeId]);
    return displayArray?.length ? displayArray.join('<br>') : '';
  }

  private scrollIntoView() {
    this.utilsService.scrollToElement(this.projectsTable.nativeElement, 145);
  }
}
